// src/pages/dashboard/Orders.jsx
import React, { useEffect, useState, useCallback, useMemo } from 'react'
import {
  Package, Search, Loader2, X, Truck, CreditCard, CheckCircle2,
  Clock, XCircle, Mail, Phone, Receipt, ArrowUpDown, AlertCircle,
} from 'lucide-react'
import api from '../../lib/axios'
import { cn } from '../../lib/utils'

const fmt = (n, currency = 'MAD') => {
  const sym = currency === 'MAD' ? 'DH' : currency === 'EUR' ? '€' : currency
  return Number(n).toFixed(2) + ' ' + sym
}

const STATUS_META = {
  pending:    { label: 'En attente',     color: 'bg-amber-50 text-amber-700 border-amber-200',      icon: Clock },
  confirmed:  { label: 'Confirmée',      color: 'bg-sky-50 text-sky-700 border-sky-200',            icon: CheckCircle2 },
  processing: { label: 'En préparation', color: 'bg-indigo-50 text-indigo-700 border-indigo-200',   icon: Package },
  shipped:    { label: 'Expédiée',       color: 'bg-blue-50 text-blue-700 border-blue-200',         icon: Truck },
  delivered:  { label: 'Livrée',         color: 'bg-emerald-50 text-emerald-700 border-emerald-200',icon: CheckCircle2 },
  cancelled:  { label: 'Annulée',        color: 'bg-red-50 text-red-700 border-red-200',            icon: XCircle },
}

const PAYMENT_META = {
  unpaid:   { label: 'Non payée',  color: 'bg-slate-100 text-slate-600' },
  paid:     { label: 'Payée',      color: 'bg-emerald-100 text-emerald-700' },
  refunded: { label: 'Remboursée', color: 'bg-amber-100 text-amber-700' },
}

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending
  const Icon = meta.icon
  return (
    <span className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border', meta.color)}>
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  )
}

const PaymentBadge = ({ status }) => {
  const meta = PAYMENT_META[status] || PAYMENT_META.unpaid
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide', meta.color)}>
      {meta.label}
    </span>
  )
}

function OrderDetailModal({ order, onClose, onCancel, cancelling }) {
  if (!order) return null
  const canCancel = ['pending', 'confirmed'].includes(order.status)

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-[#000a1e]/60 backdrop-blur-sm animate-fade-in" />
      <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-white rounded-3xl shadow-2xl p-7 sm:p-8 animate-pop">
        <button onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 hover:rotate-90 transition-all">
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3 mb-6">
          <div className="w-11 h-11 rounded-2xl bg-[#006b5f]/10 flex items-center justify-center">
            <Receipt className="w-5 h-5 text-[#006b5f]" />
          </div>
          <div>
            <h3 className="font-display font-bold text-xl text-[#000a1e]">Commande {order.reference}</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Passée le {new Date(order.created_at).toLocaleString('fr-FR')}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-6">
          <StatusBadge status={order.status} />
          <PaymentBadge status={order.payment_status} />
        </div>
        <div className="flex items-center gap-4 p-4 bg-[#eff2ff] rounded-2xl mb-5">
          {order.product?.image ? (
            <img src={order.product.image} alt="" className="w-16 h-16 rounded-xl object-cover" />
          ) : (
            <div className="w-16 h-16 rounded-xl bg-white flex items-center justify-center">
              <Package className="w-6 h-6 text-[#006b5f]" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-[#000a1e] truncate">{order.product?.name || 'Produit'}</p>
            <p className="text-xs text-slate-500 mt-0.5">{order.quantity} × {fmt(order.unit_price, order.currency)}</p>
          </div>
          <p className="font-display font-bold text-lg text-[#000a1e]">{fmt(order.total, order.currency)}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          <div className="p-4 border border-slate-100 rounded-2xl">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Client</p>
            <p className="text-sm font-semibold text-[#000a1e]">{order.customer.name}</p>
            <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-1">
              <Mail className="w-3 h-3" /> {order.customer.email}
            </p>
            {order.customer.phone && (
              <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-1">
                <Phone className="w-3 h-3" /> {order.customer.phone}
              </p>
            )}
          </div>
          <div className="p-4 border border-slate-100 rounded-2xl">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Livraison</p>
            {order.tracking?.number ? (
              <>
                <p className="text-sm font-semibold text-[#000a1e] flex items-center gap-1.5">
                  <Truck className="w-3.5 h-3.5 text-[#006b5f]" /> {order.tracking.number}
                </p>
                {order.tracking.carrier && (
                  <p className="text-xs text-slate-500 mt-1">{order.tracking.carrier}</p>
                )}
              </>
            ) : (
              <p className="text-xs text-slate-400 italic">Aucun numéro de suivi</p>
            )}
          </div>
        </div>
        <div className="p-4 border border-slate-100 rounded-2xl mb-5">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-3">Historique</p>
          <div className="space-y-2 text-xs">
            {order.paid_at && (
              <div className="flex items-center gap-2 text-slate-600">
                <CreditCard className="w-3.5 h-3.5 text-emerald-500" />
                Payée le {new Date(order.paid_at).toLocaleString('fr-FR')}
              </div>
            )}
            {order.shipped_at && (
              <div className="flex items-center gap-2 text-slate-600">
                <Truck className="w-3.5 h-3.5 text-blue-500" />
                Expédiée le {new Date(order.shipped_at).toLocaleString('fr-FR')}
              </div>
            )}
            {order.delivered_at && (
              <div className="flex items-center gap-2 text-slate-600">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                Livrée le {new Date(order.delivered_at).toLocaleString('fr-FR')}
              </div>
            )}
            {order.cancelled_at && (
              <div className="flex items-center gap-2 text-slate-600">
                <XCircle className="w-3.5 h-3.5 text-red-500" />
                Annulée le {new Date(order.cancelled_at).toLocaleString('fr-FR')}
              </div>
            )}
            {!order.paid_at && !order.shipped_at && !order.delivered_at && !order.cancelled_at && (
              <p className="text-slate-400 italic">Aucun événement pour le moment.</p>
            )}
          </div>
        </div>
        {order.notes && (
          <div className="p-4 bg-amber-50 border border-amber-100 rounded-2xl text-xs text-amber-800 mb-5">
            <p className="font-semibold mb-1">Note du client</p>
            {order.notes}
          </div>
        )}
        {canCancel && (
          <button onClick={() => onCancel(order)} disabled={cancelling}
            className="w-full py-3 rounded-xl border border-red-200 text-red-600 font-semibold text-sm hover:bg-red-50 transition disabled:opacity-60 flex items-center justify-center gap-2">
            {cancelling && <Loader2 className="w-4 h-4 animate-spin" />}
            Annuler cette commande
          </button>
        )}
      </div>
    </div>
  )
}

const FILTERS = [
  { id: 'all', label: 'Toutes' },
  { id: 'pending', label: 'En attente' },
  { id: 'confirmed', label: 'Confirmées' },
  { id: 'processing', label: 'En préparation' },
  { id: 'shipped', label: 'Expédiées' },
  { id: 'delivered', label: 'Livrées' },
  { id: 'cancelled', label: 'Annulées' },
]

export default function Orders() {
  const [orders, setOrders]     = useState([])
  const [loading, setLoading]   = useState(true)
  const [error, setError]       = useState('')
  const [filter, setFilter]     = useState('all')
  const [search, setSearch]     = useState('')
  const [selected, setSelected] = useState(null)
  const [sortDesc, setSortDesc] = useState(true)
  const [cancelling, setCancelling] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const { data } = await api.get('/orders', {
        params: {
          status: filter !== 'all' ? filter : undefined,
          q: search || undefined,
          per_page: 50,
        },
      })
      setOrders(data?.data ?? [])
    } catch (e) {
      setError('Impossible de charger vos commandes.')
    } finally {
      setLoading(false)
    }
  }, [filter, search])

  useEffect(() => {
    const t = setTimeout(load, 250)
    return () => clearTimeout(t)
  }, [load])

  const sorted = useMemo(() => {
    const arr = [...orders]
    arr.sort((a, b) => {
      const da = new Date(a.created_at).getTime()
      const db = new Date(b.created_at).getTime()
      return sortDesc ? db - da : da - db
    })
    return arr
  }, [orders, sortDesc])

  const handleCancel = async (order) => {
    if (!window.confirm(`Annuler la commande ${order.reference} ?`)) return
    setCancelling(true)
    try {
      await api.post(`/orders/${order.id}/cancel`)
      setSelected(null)
      load()
    } catch (e) {
      alert(e?.response?.data?.message || 'Impossible d\'annuler cette commande.')
    } finally {
      setCancelling(false)
    }
  }

  const stats = useMemo(() => ({
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    shipped: orders.filter(o => o.status === 'shipped').length,
    delivered: orders.filter(o => o.status === 'delivered').length,
  }), [orders])

  return (
    <div className="min-h-screen bg-[#f9f9ff]">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@500;600;700;800&display=swap');
        .font-display{font-family:'Plus Jakarta Sans',system-ui,sans-serif}
        .font-body{font-family:'Inter',system-ui,sans-serif}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        @keyframes pop{0%{opacity:0;transform:scale(.92) translateY(8px)}100%{opacity:1;transform:scale(1) translateY(0)}}
        .animate-fade-in{animation:fadeIn .25s ease-out both}
        .animate-pop{animation:pop .3s ease-out both}
      `}</style>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 font-body">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display font-bold text-3xl text-[#000a1e] tracking-tight flex items-center gap-2">
              <Package className="w-7 h-7 text-[#006b5f]" /> Mes commandes
            </h1>
            <p className="text-sm text-slate-500 mt-1">Suivez l'état de vos commandes Tapio en temps réel.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Référence, produit…"
                className="pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm w-full sm:w-72 focus:outline-none focus:ring-4 focus:ring-[#006b5f]/15 focus:border-[#006b5f]" />
            </div>
            <button onClick={() => setSortDesc(v => !v)}
              className="p-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition" title="Inverser le tri">
              <ArrowUpDown className="w-4 h-4 text-slate-500" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total', value: stats.total, color: 'text-[#000a1e]' },
            { label: 'En attente', value: stats.pending, color: 'text-amber-600' },
            { label: 'Expédiées', value: stats.shipped, color: 'text-blue-600' },
            { label: 'Livrées', value: stats.delivered, color: 'text-emerald-600' },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{s.label}</p>
              <p className={cn('font-display font-bold text-2xl mt-1', s.color)}>{s.value}</p>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-2 flex gap-1 overflow-x-auto mb-6 shadow-sm">
          {FILTERS.map((f) => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              className={cn(
                'px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition',
                filter === f.id
                  ? 'bg-[#006b5f] text-white shadow-md shadow-[#006b5f]/20'
                  : 'text-slate-600 hover:bg-slate-50'
              )}>
              {f.label}
            </button>
          ))}
        </div>

        {loading && (
          <div className="flex justify-center py-24">
            <Loader2 className="w-8 h-8 text-[#006b5f] animate-spin" />
          </div>
        )}
        {error && (
          <div className="max-w-lg mx-auto p-4 bg-red-50 border border-red-100 rounded-2xl text-sm text-red-600 text-center flex items-center gap-2 justify-center">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}
        {!loading && !error && sorted.length === 0 && (
          <div className="text-center py-20">
            <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Aucune commande pour le moment.</p>
          </div>
        )}
        {!loading && sorted.length > 0 && (
          <div className="space-y-3">
            {sorted.map((order) => (
              <div key={order.id} onClick={() => setSelected(order)}
                className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer flex items-center gap-4">
                {order.product?.image ? (
                  <img src={order.product.image} alt="" className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
                ) : (
                  <div className="w-14 h-14 rounded-xl bg-[#eff2ff] flex items-center justify-center flex-shrink-0">
                    <Package className="w-6 h-6 text-[#006b5f]/40" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-mono text-xs font-bold text-[#006b5f]">{order.reference}</span>
                    <StatusBadge status={order.status} />
                  </div>
                  <p className="font-semibold text-[#000a1e] truncate">{order.product?.name || 'Produit'}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {order.quantity} × {fmt(order.unit_price, order.currency)} · {new Date(order.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-display font-bold text-lg text-[#000a1e]">{fmt(order.total, order.currency)}</p>
                  <PaymentBadge status={order.payment_status} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <OrderDetailModal
          order={selected}
          onClose={() => setSelected(null)}
          onCancel={handleCancel}
          cancelling={cancelling}
        />
      )}
    </div>
  )
}